import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

/* Live signals: motion is feedback (a count moves because a capture arrived). */

const reduced = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** A number that rolls to its new value and glows briefly when it grows. */
export function CountUp({ value, className }: { value: number; className?: string }) {
    const [shown, setShown] = useState(value);
    const [bump, setBump] = useState(false);
    const from = useRef(value);
    useEffect(() => {
        const start = from.current;
        from.current = value;
        if (start === value) return;
        if (value > start) {
            setBump(true);
            const t = window.setTimeout(() => setBump(false), 900);
            if (reduced()) {
                setShown(value);
                return () => window.clearTimeout(t);
            }
            const t0 = performance.now();
            let raf = 0;
            const step = (now: number) => {
                const p = Math.min(1, (now - t0) / 700);
                const eased = 1 - Math.pow(1 - p, 3);
                setShown(Math.round(start + (value - start) * eased));
                if (p < 1) raf = requestAnimationFrame(step);
            };
            raf = requestAnimationFrame(step);
            return () => {
                cancelAnimationFrame(raf);
                window.clearTimeout(t);
            };
        }
        setShown(value);
    }, [value]);
    return (
        <span className={`countup${bump ? ' bump' : ''}${className ? ` ${className}` : ''}`}>
            {shown.toLocaleString()}
        </span>
    );
}

/** Captures over time as a soft area line in the event's color. */
export function Sparkline({
    points,
    height = 36,
    label,
}: {
    points: number[];
    height?: number;
    label?: string;
}) {
    if (points.length < 2) return null;
    const W = 120;
    const max = Math.max(1, ...points);
    const xy = points.map((v, i) => [
        (i / (points.length - 1)) * W,
        height - 2 - (v / max) * (height - 6),
    ]);
    const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `0,${height} ${line} ${W},${height}`;
    const [lx, ly] = xy[xy.length - 1];
    return (
        <svg
            className="spark"
            viewBox={`0 0 ${W} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={label ?? 'Captures over the last hours'}
        >
            <polygon points={area} fill="var(--event)" opacity="0.14" />
            <polyline points={line} fill="none" stroke="var(--event)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
            <circle cx={lx} cy={ly} r="2.4" fill="var(--event)" />
        </svg>
    );
}

/* stable fallback when an event has no gallery color yet */
const HAND = ['#E64389', '#334A9C', '#C42914', '#28AFA5', '#1393DD'];
export function handColor(key: number | string): string {
    const n =
        typeof key === 'number'
            ? key
            : Array.from(key).reduce((a, c) => a + c.charCodeAt(0), 0);
    return HAND[Math.abs(n) % HAND.length];
}

/** Row lead: the event's latest photo, else its color with its initial. */
export function RowLead({
    cover,
    color,
    name,
}: {
    cover?: string | null;
    color?: string;
    name: string;
}) {
    return (
        <span
            className="rlead"
            aria-hidden="true"
            style={{ '--lead': color } as React.CSSProperties}
        >
            {cover ? (
                <img src={cover} alt="" loading="lazy" />
            ) : (
                (name.trim()[0] ?? '·').toUpperCase()
            )}
        </span>
    );
}
